import { z } from "zod";
const CtaSchema = z.object({
    label: z.string().min(1).max(80),
    href: z
        .string()
        .min(1)
        .max(300)
        .regex(/^(https?:\/\/|\/|#|mailto:)/, "href must be a relative path, anchor, https URL, or mailto"),
});
export const WebsitePageMetaSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(300).optional(),
    // Relative output path within the site repo, e.g. "index.html" or "services/growth-belt.html".
    path: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[a-z0-9][a-z0-9/_-]*\.html$/, "path must be a relative .html file"),
});
const HeroFields = z.object({
    eyebrow: z.string().max(120).optional(),
    heading: z.string().min(1).max(200),
    highlight: z.string().max(200).optional(),
    subheading: z.string().max(600).optional(),
    primaryCta: CtaSchema.optional(),
    secondaryCta: CtaSchema.optional(),
});
const StatsFields = z.object({
    eyebrow: z.string().max(120).optional(),
    heading: z.string().max(200).optional(),
    items: z
        .array(z.object({ value: z.string().min(1).max(40), label: z.string().min(1).max(120) }))
        .min(1)
        .max(6),
});
const ThesisFields = z.object({
    eyebrow: z.string().max(120).optional(),
    heading: z.string().min(1).max(200),
    body: z.string().min(1).max(4000),
});
const CardsFields = z.object({
    eyebrow: z.string().max(120).optional(),
    heading: z.string().max(200).optional(),
    cards: z
        .array(z.object({
        title: z.string().min(1).max(160),
        body: z.string().min(1).max(2000),
        href: z.string().max(300).optional(),
    }))
        .min(1)
        .max(12),
});
const ItemsFields = z.object({
    eyebrow: z.string().max(120).optional(),
    heading: z.string().max(200).optional(),
    items: z
        .array(z.object({ title: z.string().min(1).max(160), body: z.string().min(1).max(2000) }))
        .min(1)
        .max(12),
});
const CtaBandFields = z.object({
    heading: z.string().min(1).max(200),
    body: z.string().max(2000).optional(),
    cta: CtaSchema,
});
const EnquiryFormFields = z.object({
    heading: z.string().max(200).optional(),
    intro: z.string().max(2000).optional(),
    consentLabel: z.string().max(300).optional(),
    submitLabel: z.string().max(80).optional(),
});
const CustomHtmlFields = z.object({
    html: z.string().max(200_000),
    // Trusted content (imported from our own site) is rendered verbatim;
    // untrusted content is sanitised at publish time (Slice 6).
    trusted: z.boolean().default(false),
});
function block(type, fields) {
    return z.object({ id: z.string().min(1).max(64), type: z.literal(type), fields });
}
export const WebsiteBlockTypeSchema = z.enum([
    "hero",
    "stats",
    "thesis",
    "workCards",
    "differentiators",
    "pillars",
    "cta",
    "enquiryForm",
    "customHtml",
]);
export const WebsiteBlockSchema = z.discriminatedUnion("type", [
    block("hero", HeroFields),
    block("stats", StatsFields),
    block("thesis", ThesisFields),
    block("workCards", CardsFields),
    block("differentiators", ItemsFields),
    block("pillars", ItemsFields),
    block("cta", CtaBandFields),
    block("enquiryForm", EnquiryFormFields),
    block("customHtml", CustomHtmlFields),
]);
export const WebsitePageDocumentSchema = z.object({
    meta: WebsitePageMetaSchema,
    blocks: z.array(WebsiteBlockSchema).max(60),
});
// WebsiteBlockType is declared above so this Partial<Record<...>> reference is valid.
export const WEBSITE_RICH_TEXT_FIELDS = {
    thesis: ["body"],
    workCards: ["cards[].body"],
    differentiators: ["items[].body"],
    pillars: ["items[].body"],
    cta: ["body"],
    enquiryForm: ["intro"],
};
//# sourceMappingURL=website-page.js.map